import { useQuery } from '@tanstack/react-query'
import { flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, useReactTable } from '@tanstack/react-table'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import CopyButton from 'components/reusable/CopyButton'
import { partnersProspects } from 'rails_routes'
import { Button, ButtonGroup, Modal, Pagination, Table } from 'slab'
import { CENSUS_EXAMPLE_PATH, CENSUS_TEMPLATE_PATH, fetchWithHeaders } from 'utils'
import { useDeleteQuotingProspectsProspectIdUploadsCensus, useGetQuotingProspectsCensusColumns, usePostQuotingProspectsProspectIdUploadsCensus } from 'utils/web_api'
import { type QuotingCensusColumn, QuotingProspectQuoteRequestStatus } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { QuoteRequestStep } from '..'
import { useProspect } from '../ProspectProvider'
import { QuoteRequestUploadPage, type UploadError } from '../QuoteRequestUploadPage'
import { useQuoteRequestProgress } from '../useQuoteRequestProgress'

interface CensusUploadPageSectionProps {
  heading: React.ReactNode
  subheading: string
  children: React.ReactNode
}

const CensusUploadPageSection = ({
  heading,
  subheading,
  children,
  ...restProps
}: CensusUploadPageSectionProps & React.HTMLAttributes<HTMLDivElement>) => (
  <div {...restProps}>
    <h2 className='text-lg font-medium text-gray-700 mb-2'>{heading}</h2>
    <p className='text-xs text-ds-text-secondary font-normal mb-4'>
      {subheading}
    </p>
    {children}
  </div>
)

const CensusFieldRow = ({
  column: { name, description, required }
}: {
  column: QuotingCensusColumn
}) => (
  <Table.Row key={name}>
    <Table.Cell style={{ padding: '4px', paddingLeft: 0 }}>
      <div className='flex flex-row items-center min-w-32'>
        <p className='text-xs text-wrap font-medium shrink break-all'>
          {name}
          {required ? <span className='text-red-400'>*</span> : undefined}
        </p>
        <CopyButton variant='default' valueToCopy={name} />
      </div>
    </Table.Cell>
    <Table.Cell style={{ padding: '4px', paddingRight: 0 }}>
      <p className='text-xs text-gray-800'>{description ?? ''}</p>
    </Table.Cell>
  </Table.Row>
)

const SKELETON_WIDTHS = ['w-1/2', 'w-2/3', 'w-3/4', 'w-11/12']

const getRandomWidthClass = () => {
  const randomIndex = Math.floor(Math.random() * SKELETON_WIDTHS.length)
  return SKELETON_WIDTHS[randomIndex]
}

const SkeletonRow = () => (
  <Table.Row>
    <Table.Cell style={{ padding: '4px', paddingLeft: 0 }}>
      <div className='h-6 w-32'>
        <div className={`bg-gray-200 rounded animate-pulse h-full ${getRandomWidthClass()}`} />
      </div>
    </Table.Cell>
    <Table.Cell style={{ padding: '4px', paddingRight: 0 }}>
      <div className='h-6 w-64'>
        <div className={`bg-gray-200 rounded animate-pulse h-full ${getRandomWidthClass()}`} />
      </div>
    </Table.Cell>
  </Table.Row>
)

interface CensusUploadPageProps {
  accept?: string[]
  returnToProspect?: boolean
}

export function CensusUploadPage ({ accept, returnToProspect }: CensusUploadPageProps) {
  const { assign } = useWindowLocation()
  const { prospect, refetch } = useProspect()
  const navigate = useNavigate()
  const { completeStep } = useQuoteRequestProgress({
    step: QuotingProspectQuoteRequestStatus.census,
    onSuccess: () => navigate(`../../${prospect?.offers_health_insurance_today ? QuoteRequestStep.SBCS_UPLOAD : QuoteRequestStep.OTHER_FILES_UPLOAD}`)
  })
  const [isUploading, setUploading] = useState(false)
  const formDataRef = useRef<FormData>()
  const { data: censusColumns = [], isLoading, error: loadError } = useGetQuotingProspectsCensusColumns()
  const { data: exampleCsv, isLoading: isLoadingExampleCsv } = useQuery({
    queryKey: ['census-example'],
    queryFn: async () => {
      const response = await fetchWithHeaders(CENSUS_EXAMPLE_PATH)
      if (!response.ok) {
        throw new Error('Failed to fetch example census')
      }

      return response.text()
    },
    select: (csv) => {
      const [headers, ...rows] = csv.split('\n')
        .filter(line => line)
        .map(row => row.split(','))
      return { headers, rows }
    }
  })
  const [isExampleModalOpen, setExampleModalOpen] = useState(false)
  const [error, setError] = useState<UploadError>()

  if (!prospect) {
    return null
  }

  const { mutate: uploadCensus } = usePostQuotingProspectsProspectIdUploadsCensus({
    mutation: {
      onSuccess: (data) => {
        setUploading(false)
        window.segment?.track('Census upload succeeded', {
          id: prospect?.id,
          contentType: data.census_upload?.content_type,
          warnings: data.census_upload_warnings
        })
        refetch()
        if (data.census_upload_warnings?.length) {
          setError({
            type: 'notice',
            title: 'Note',
            errors: data.census_upload_warnings
          })
        }
      },
      onError: (err: GrapeAPIErrorType) => {
        setUploading(false)
        window.segment?.track('Census upload failed', { id: prospect?.id, errors: err.errors })
        setError({
          title: err.message,
          errors: err.errors
        })
      }
    },
    request: {
      get body () { return formDataRef.current }
    }
  })

  const { mutate: deleteCensus } = useDeleteQuotingProspectsProspectIdUploadsCensus({
    mutation: {
      onSuccess: () => {
        window.segment?.track('Census deleted', { id: prospect?.id })
        refetch()
      },
      onError: (err: GrapeAPIErrorType) => {
        setError({
          title: err.message,
          errors: err.errors
        })
      }
    },
  })

  useEffect(() => {
    if (loadError) {
      setError({
        title: 'Error loading required census columns. Please refresh the page.',
        errors: []
      })
    }
  })

  const columns = exampleCsv?.headers.map((header, i) => ({
    header, accessorKey: `${i}`,
  })) || []

  const table = useReactTable({
    columns,
    data: exampleCsv?.rows || [],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const requiredColumns = useMemo(() => censusColumns.filter(column => column.required), [censusColumns])
  const optionalColumns = useMemo(() => censusColumns.filter(column => !column.required), [censusColumns])

  const onBack = () => {
    if (returnToProspect) {
      assign(partnersProspects.show({ id: prospect.id }))
    } else {
      navigate(`../../${QuoteRequestStep.CLIENT_DETAILS}`)
    }
  }

  const onNext = () => {
    if (!prospect.census_upload) {
      setError({
        title: "A census is required. Either download and fill out Thatch's census template or use an existing census with the provided column names.",
        errors: []
      })
    } else if (returnToProspect) {
      assign(partnersProspects.show({ id: prospect.id }))
    } else {
      completeStep()
    }
  }

  const onUpload = (files: File[]) => {
    setError(undefined)
    const formData = new FormData()
    formData.append('file', files[0])
    formDataRef.current = formData
    setUploading(true)
    window.segment?.track('Census upload attempted', { id: prospect?.id })
    uploadCensus({
      prospectId: prospect.id,
    })
  }

  const onDelete = () => {
    setError(undefined)
    if (prospect.census_upload) {
      deleteCensus({
        prospectId: prospect.id,
      })
    }
  }

  return (
    <QuoteRequestUploadPage
      title='Upload employee census'
      files={prospect.census_upload ? [prospect.census_upload] : []}
      accept={accept?.join(',')}
      multiple={false}
      isUploading={isUploading}
      onUpload={onUpload}
      onDelete={onDelete}
      onBack={onBack}
      onNext={onNext}
      nextButtonText={returnToProspect ? 'Return' : undefined}
      canProceed={!!prospect.census_upload}
      error={error}
    >
      <CensusUploadPageSection
        heading='Formatting the census'
        subheading={"A well-formatted census ensures a quick turnaround time \
          from the Thatch team. You can either utilize Thatch's template or \
          rename the columns in an existing census file."}
        className='mb-10'
      >
        <ButtonGroup align='left'>
          <a href={CENSUS_TEMPLATE_PATH} download={CENSUS_TEMPLATE_PATH.split('/').pop()}>
            <Button variant={prospect.census_upload ? 'secondary' : 'primary'}>Download census template</Button>
          </a>
          <Button variant='tertiary' onClick={() => setExampleModalOpen(true)}>See example</Button>
        </ButtonGroup>
      </CensusUploadPageSection>

      <CensusUploadPageSection
        heading={<>Required columns <span className='text-red-400'>*</span></>}
        subheading='These columns are required for every census.'
        className='mb-6'
      >
        <Table>
          <Table.Body>
            {isLoading
              ? Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
              : requiredColumns.map(column => <CensusFieldRow key={column.name} column={column} />)}

            {/* Fake row used here for the optional section header to preserve indentation */}
            <Table.Row>
              <Table.Cell style={{ padding: 0, borderBottom: 'none' }} colSpan={2}>
                <h2 className='text-lg font-medium text-gray-700 mt-6 mb-2'>Optional fields</h2>
                <p className='text-xs text-ds-text-secondary font-normal mb-4 w-full'>
                  While optional, these fields help produce a more accurate quote.
                </p>
              </Table.Cell>
            </Table.Row>

            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={`opt-${i}`} />)
              : optionalColumns.map(column => <CensusFieldRow key={column.name} column={column} />)}
          </Table.Body>
        </Table>
      </CensusUploadPageSection>
      <Modal
        open={isExampleModalOpen}
        onOpenChange={setExampleModalOpen}
        width='full'
      >
        <Modal.Body>
          <Modal.Title>Example census</Modal.Title>
          <Modal.Description>
            <p className='text-xs text-gray-800 font-normal'>
              In this example, John and Maria are employees. Patrick and Jack Gould are dependents of Maria: Patrick is her spouse and Jack is their child.
            </p>
          </Modal.Description>
          <div className='overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'>
            <Table id='employers-table' data-testid='employers-table'>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.ColumnHeader key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</Table.ColumnHeader>
                    ))}
                  </Table.Row>
                ))}
              </Table.Head>
              <Table.Body>
                {!isLoadingExampleCsv && (
                  table.getRowModel().rows.map((row) => (
                    <Table.Row
                      key={row.id} accessibleLabel={row.original.join(',')}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Cell>
                      ))}
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
            <div className='flex justify-end'>
              <Pagination
                pageIndex={table.getState().pagination.pageIndex}
                perPage={table.getState().pagination.pageSize}
                totalRows={table.getRowCount()}
                onPageChange={(pageIndex: number) => table.setPageIndex(pageIndex)}
              />
            </div>
          </div>
          <ButtonGroup align='right'>
            <Button
              variant='primary'
              size='large'
              onClick={() => setExampleModalOpen(false)}
            >Back
            </Button>
          </ButtonGroup>
        </Modal.Body>
      </Modal>
    </QuoteRequestUploadPage>
  )
}
